import './challenges.css';

import EventEmitter from 'eventemitter3';
import { OrderService } from '../../../services/order-service';
import { Modal } from '../modal';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { DataLayerService } from '../../../services/data-layer-service';
import { IChallenges, IChallengesOpenFieldData, IChallengesProps } from './challenges.interface';
import { Util } from '../../../utils/util';
import { IDataChallenge, IDataChallengeEvent, IDataChallengeMember, IDataChallengeSubMember } from '../../../models/data.interface';
import { Data } from '../../../models/data';
import { ChallengeFieldType, Modals } from '../../../types/enums';
import { DialogCreators } from '../../../utils/dialog-creators';
import { EventTypes } from '../../../core/event-bus';
import { IOrderChallengeEvent, IOrderChallengeMember } from '../../../models/order.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Manages the Challenges modal, allowing users to select challenge members and events.
 */
export class Challenges extends Modal {
    private readonly _data: Data;
    private readonly _orderService: OrderService;
    private _bindingHelper: TwoWayBindingHelper<IChallenges>;
    private _item: IChallenges;
    private _options?: IChallengesProps;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, orderService: OrderService, dataLayer: DataLayerService) {
        super("modal_challenges", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Challenges" }, Modals.CHALLENGES);
        this._data = data;
        this._orderService = orderService;
        this._item = this._createModel();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setTwoWayListeners();
        this._setEventBusListener();
    }

    /**
     * Initializes the data model used for binding.
     * @private
     * @returns {IChallenges} The initial state of the challenge form.
     */
    private _createModel(): IChallenges {
        return {
            challenge: null,
            search: "",
            members: []
        };
    }

    /**
     * Sets up two-way data bindings for form fields.
     * @private
     */
    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("search", "txt_challenges_search");
    }

    /**
     * Registers DOM element event listeners for user interactions.
     * @protected
     */
    protected _setEventListeners(): void {
        Util.setElement("oninput", "txt_challenges_search", this._searchFunctionality);
        Util.setElement("onclick", "btn_challenges_apply", this._applyChallenge);
    }

    /**
     * Registers modal open event listener to store modal-specific options.
     * @protected
     */
    protected _setEventBusListener = (): void => {
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: IChallengesProps) => {
            if (modal === Modals.CHALLENGES)
                this._options = options;
        });
    }

    /**
     * Initializes the modal's UI and data bindings.
     * @public
     */
    public override init(): void {
        super.init();
        this._renderPage();
    }

    /**
     * Opens the modal and configures it using the stored options.
     * @public
     */
    public override open(): void {
        this._configureOptions();
        this._setEventListeners();
        this.init();        
        super.open();
    }

    /**
     * Applies modal options such as the challenge to display.
     * @private
     */
    private _configureOptions(): void {
        this._item.challenge = this._options?.challenge || null;
    }

    /**
     * Renders the page UI for available challenge members and events.
     * @private
     */
    private _renderPage(): void {
        if (!this._item.challenge)
            return;

        Util.setElement("innerText", "div_challenges_header", this._localizationService.GetNewName(this._item.challenge.Names));

        const membersDiv = document.getElementById("div_challenges_members");
        if (!membersDiv)
            return;

        membersDiv.innerHTML = "";

        for (const member of this._item.challenge.Members) {
            membersDiv.appendChild(this._createMemberOption(this._item.challenge, member));
        }
    }

    /**
     * Handles the removal of an open field input element in the UI.
     * @param {Event} event - The event triggered by clicking the remove button.
     */
    private readonly _handleOpenFieldRemove = (event: Event) => {
        const element = <HTMLInputElement>event?.target;
        if (!element?.parentElement)
            return;

        element.parentElement.remove();
    }

    /**
     * Handles the addition of a new open input field for user input.
     * @param {Event} event - The triggering event from the UI.
     * @param {IChallengesOpenFieldData} value - Metadata needed to construct the input field.
     */
    private readonly _handleOpenFieldAdd = (event: Event, value: IChallengesOpenFieldData) => {
        const input = <HTMLInputElement>event?.target;
        if (input == null)
            return;

        input.classList.add("hidden");

        const buttonsName = `btn_challenge_open_field_${value.memberId}_${value.eventId}_${(value.subMemberId && value.isExtraField) ? `${value.subMemberId}` : "subMember"}`;

        const removeButtons = document.getElementsByName(`${buttonsName}_remove`);
        removeButtons.forEach(x => { x.classList.remove('hidden'); });

        const addButtons = document.getElementsByName(`${buttonsName}_add`);
        addButtons.forEach(x => { x.classList.add('hidden'); });

        const container = document.getElementById(value.containerId);
        if (container) {
            const data = JSON.parse(JSON.stringify(value)) as IChallengesOpenFieldData;
            data.title = null;
            data.index = data.index + 1;
            data.preValue = "";

            container.appendChild(this._createOpenField(data));
        }
    }

    /**
     * Creates a UI section for a single challenge member and its valid events.
     * @private
     * @param {IDataChallenge} challenge - The full challenge object.
     * @param {IDataChallengeMember} member - A member associated with the challenge.
     * @returns {HTMLElement} The DOM element representing the member block.
     */
    private _createMemberOption(challenge: IDataChallenge, member: IDataChallengeMember): HTMLElement {
        const orderChallenge = this._orderService.order.getProperty("Challenges").find(x => x.challengeId === challenge.ChallengeId);
        const orderMember = orderChallenge?.members.find(x => x.memberId === member.MemberId) || null;

        const memberDiv = document.createElement("div");
        memberDiv.id = `div_challenge_event_${member.MemberId}`;
        memberDiv.classList.add("member-check");

        const memberChecked = Boolean(orderMember);
        const memberCheckbox = this._createMemberCheckbox(member, memberChecked);
        memberDiv.appendChild(memberCheckbox);

        const businessDate = Util.getBusinessDate(this._data);
        const activeEvents = member.Events.filter(evt => Util.compareDates(new Date(evt.EventDate), businessDate) === 0);

        for (const event of activeEvents) {
            const eventOption = this._createEventOption(member.MemberId, event, orderMember);
            memberDiv.appendChild(eventOption);
        }

        return memberDiv;
    }

    /**
     * Creates the checkbox element for a challenge member.
     * Toggles associated event elements on click.
     * 
     * @param {IDataChallengeMember} member - The challenge member to render.
     * @param {boolean} checked - Whether the checkbox should be initially checked.
     * @returns {HTMLElement} The member checkbox element.
     */
    private _createMemberCheckbox(member: IDataChallengeMember, checked: boolean): HTMLElement {
        const id = `chk_challenge_member_${member.MemberId}`;
        const html = `
            <div class="checkRadio">
                <input type="checkbox" id="${id}" class="hide-show-siblings" ${checked ? `checked="true"` : ""} />
                <label for="${id}">${member.Name}</label>
            </div>`;

        const container = Util.createHtmlElementFromTemplate(html);
        const checkbox = container.querySelector(`#${id}`) as HTMLInputElement;

        if (checkbox)
            checkbox.onclick = () => {
                const events = document.getElementsByName(`div_challenge_event_${member.MemberId}`);
                events.forEach(x => x.classList.toggle("hidden"));
            };

        return container;
    }

    /**
     * Creates a UI block representing a challenge event and its related inputs.
     *
     * @param {number} memberId - The ID of the parent member.
     * @param {IDataChallengeEvent} event - The challenge event to render.
     * @param {IOrderChallengeMember | null} orderMember - The selected order member data (if any).
     * @returns {HTMLElement} The event DOM block.
     */
    private _createEventOption(memberId: number, event: IDataChallengeEvent, orderMember: IOrderChallengeMember | null): HTMLElement {
        const orderEvent = orderMember?.events.find(x => x.eventId == event.EventId);
        const memberChecked = !!orderMember;
        const eventChecked = memberChecked ? (orderEvent ? true : false) : false;

        const eventBlock = document.createElement("div");
        eventBlock.setAttribute("name", `div_challenge_event_${memberId}`);
        eventBlock.classList.add("event-list", "hide-show-onchecked");
        if (!memberChecked)
            eventBlock.classList.add("hidden");

        const eventCheckbox = this._createEventCheckbox(memberId, event, eventChecked);
        eventBlock.appendChild(eventCheckbox);

        if (event.EntryTypeId === ChallengeFieldType.OPEN_FIELD) {
            const orderSubmember = orderEvent?.subMember ?? null;
            eventBlock.appendChild(this._createEventOpenField(memberId, event, orderSubmember));
        } else if (event.EntryTypeId === ChallengeFieldType.CHECKBOX && event.SubMembers && event.SubMembers.length > 0) {
            eventBlock.appendChild(this._createEventSubMembers(memberId, event, orderMember));
        }

        return eventBlock;
    }

    /**
     * Creates the open field input block for an event.
     *
     * @param {number} memberId - ID of the member.
     * @param {IDataChallengeEvent} event - Event data.
     * @param {string | null} orderSubmember - Previously entered value, if any.
     * @returns {HTMLElement} DOM element representing the open field input.
     */
    private _createEventOpenField(memberId: number, event: IDataChallengeEvent, orderSubmember: string | null): HTMLElement {
        const container = document.createElement("div");
        container.id = `div_challenge_open_field_${memberId}_${event.EventId}_subMember`;
        container.setAttribute("name", `div_challenge_event_${memberId}_${event.EventId}`);
        container.classList.add("hide-show-onchecked");
        if (!orderSubmember)
            container.classList.add("hidden");

        const openField = this._getCurrentOpenFields(memberId, event.EventId, null, false, event.SubMemberTitle, orderSubmember);
        container.appendChild(openField);

        return container;
    }

    /**
     * Creates the submember section for an event that supports checkboxes and optional extras.
     *
     * @param {number} memberId - ID of the parent member.
     * @param {IDataChallengeEvent} event - Event data.
     * @param {IOrderChallengeMember | null} orderMember - Member selection state (if any).
     * @returns {HTMLElement} DOM container for the submembers.
     */
    private _createEventSubMembers(memberId: number, event: IDataChallengeEvent, orderMember: IOrderChallengeMember | null): HTMLElement {
        const html = `
            <div name="div_challenge_event_${memberId}_${event.EventId}" class="hide-show-onchecked challenge-list-group ${orderMember ? "" : "hidden"}">
                <div class="group-title">
                    <label>${event.SubMemberTitle}</label>
                </div>
            </div>`;

        const container = Util.createHtmlElementFromTemplate(html);

        for (const submember of event.SubMembers) {
            const submemberEvent = orderMember?.events.find(x => x.eventId == event.EventId && x.subMemberId == submember.SubMemberId) || null;
            const subMemberDiv = this._createEventSubMember(memberId, event, submember, submemberEvent);
            container.appendChild(subMemberDiv);
        }

        return container;
    }

    /**
     * Creates a submember block for a checkbox-based challenge event.
     *
     * @param {number} memberId - Member ID.
     * @param {IDataChallengeEvent} event - The event data.
     * @param {IDataChallengeSubMember} submember - The submember data.
     * @param {IOrderChallengeEvent | null} submemberEvent - The submember’s previous selection data, if any.
     * @returns {HTMLElement} DOM element representing the submember option.
     */
    private _createEventSubMember(memberId: number, event: IDataChallengeEvent, submember: IDataChallengeSubMember, submemberEvent: IOrderChallengeEvent | null): HTMLElement {
        const container = document.createElement("div");
        container.id = `div_challenge_event_${memberId}_${event.EventId}_${submember.SubMemberId}`;
        container.classList.add("group-item");

        const subMemberDiv = this._createSubMemberCheckbox(memberId, event, submember, submemberEvent ? true : false);
        container.appendChild(subMemberDiv);

        if (submember.IsExtraField) {
            const hasPredefined = submember.PredefinedValues !== "";
            const extraValue = submemberEvent?.extra || "";

            const extraField = hasPredefined
                ? this._getEventExtraField(memberId, event.EventId, submember, extraValue)
                : this._getCurrentOpenFields(memberId, event.EventId, submember.SubMemberId, true, submember.ExtraFieldTitle, extraValue);

            container.appendChild(extraField);
        }

        return container;
    }

    /**
     * Generates one or more open input fields based on stored values.
     *
     * @param {number} memberId - ID of the member.
     * @param {number} eventId - ID of the event.
     * @param {number | null} subMemberId - ID of the submember if applicable.
     * @param {boolean} isExtraField - Indicates whether this is an extra field (vs regular input).
     * @param {string} title - Label/title for the input field.
     * @param {string | null} currentFieldValues - Comma-separated values from a previous order.
     * @returns {HTMLElement} Container with input fields.
     */
    private _getCurrentOpenFields(memberId: number, eventId: number, subMemberId: number | null, isExtraField: boolean, title: string, currentFieldValues: string | null): HTMLElement {
        const values: string[] = currentFieldValues?.split(',').map(x => x.trim()) || [];

        let addButton: boolean = true;
        let removeButton: boolean = false;

        let preValue: string = '';
        if (values.length > 0)
            preValue = values[0].trim();

        if (values.length > 1) {
            addButton = false;
            removeButton = true;
        }

        const parentDiv = document.createElement("div");
        const containerId = `div_challenge_event_${memberId}_${eventId}${subMemberId ? `_${subMemberId}_extra` : ""}`;

        const openFieldData: IChallengesOpenFieldData = {
            memberId,
            eventId,
            isExtraField,
            subMemberId,
            index: 0,
            title,
            containerId,
            preValue,
            addButton,
            removeButton
        };

        const openField = this._getEventOpenField(openFieldData);
        parentDiv.appendChild(openField);

        if (values.length > 1) {
            for (let i = 1; i < values.length; i++) {
                if (i == values.length - 1) {
                    openFieldData.addButton = true;
                    openFieldData.removeButton = false;
                }

                openFieldData.index = i;
                openFieldData.containerId = `div_challenge_open_field_${memberId}_${eventId}_${Math.floor(Math.random() * 1000000)}`;
                openFieldData.preValue = values[i];

                const filledOpenField = this._getEventOpenField(openFieldData);
                parentDiv.appendChild(filledOpenField);
            }
        }

        return parentDiv;
    }

    /**
     * Creates the checkbox input for an event.
     *
     * @param {number} memberId - ID of the parent member.
     * @param {IDataChallengeEvent} event - Event metadata.
     * @param {boolean} checked - Whether the checkbox should be pre-checked.
     * @returns {HTMLElement} DOM checkbox for the event.
     */
    private _createEventCheckbox(memberId: number, event: IDataChallengeEvent, checked: boolean): HTMLElement {
        const id = `chk_challenge_event_${memberId}_${event.EventId}`;
        const html = `
            <div class="checkRadio">
                <input type="checkbox" id="${id}" class="hide-show-siblings" ${checked ? `checked="true"` : ""} />
                <label for="${id}">${event.EventName}</label>
            </div>`;

        const container = Util.createHtmlElementFromTemplate(html);
        const checkbox = container.querySelector(`#${id}`) as HTMLInputElement;

        if (checkbox)
            checkbox.onclick = () => {
                const submembers = document.getElementsByName(`div_challenge_event_${memberId}_${event.EventId}`);
                submembers.forEach(x => x.classList.toggle("hidden"));
            };

        return container;
    }

    /**
     * Creates a checkbox or radio input for a challenge submember.
     *
     * @param {number} memberId - Parent member ID.
     * @param {IDataChallengeEvent} event - Event data.
     * @param {IDataChallengeSubMember} subMember - Submember metadata.
     * @param {boolean} checked - Whether the input should be checked.
     * @returns {HTMLElement} Checkbox/radio DOM element.
     */
    private _createSubMemberCheckbox(memberId: number, event: IDataChallengeEvent, subMember: IDataChallengeSubMember, checked: boolean): HTMLElement {
        const id = `challenge_event_${memberId}_${event.EventId}_${subMember.SubMemberId}`;
        const type = event.IsMultipleSelectionsAllowed ? "checkbox" : "radio";
        const name = event.IsMultipleSelectionsAllowed ? "" : `name="chk_challenge_event_${memberId}_${event.EventId}"`;

        const html = `
            <div class="checkRadio">
                <input type="${type}" id="chk_${id}" class="hide-show-siblings" ${checked ? `checked="true"` : ""}${name} />
                <label for="chk_${id}">${subMember.SubMemberName}</label>
            </div>`;

        const container = Util.createHtmlElementFromTemplate(html);
        const checkbox = container.querySelector(`#chk_${id}`) as HTMLInputElement;

        if (checkbox)
            checkbox.onclick = () => {
                if (event.IsMultipleSelectionsAllowed) {
                    const extras = document.getElementsByName(`div_${id}_extra`);
                    extras.forEach(x => x.classList.toggle("hidden"));
                } else {
                    const extras = Array.from(document.querySelectorAll(`[name^="div_challenge_event_${memberId}_${event.EventId}_"][name$="_extra"]`)) as HTMLElement[];
                    extras.forEach(x => Util.hideElement(x));
                    const extraGroup = document.querySelector(`[name^="div_${id}_extra"]`) as HTMLElement;
                    Util.showElement(extraGroup);
                }
            };

        return container;
    }

    /**
     * Creates predefined checkbox/radio inputs for submember extra fields.
     *
     * @param {number} memberId - ID of the member.
     * @param {number} eventId - ID of the event.
     * @param {IDataChallengeSubMember} subMember - Submember data.
     * @param {string} orderPredefinedValues - Preselected values from a prior order.
     * @returns {HTMLElement} DOM element containing predefined value options.
     */
    private _getEventExtraField(memberId: number, eventId: number, subMember: IDataChallengeSubMember, orderPredefinedValues: string): HTMLElement {
        const predefinedValues = subMember.PredefinedValues.includes("]")
            ? subMember.PredefinedValues.split("]")
            : subMember.PredefinedValues.split("\n");

        const id = `challenge_event_${memberId}_${eventId}_${subMember.SubMemberId}_extra`;
        const html = `
            <div name="div_${id}" class="hide-show-onchecked challenge-list-group ${orderPredefinedValues ? "" : "hidden"}">
                <div class="group-title">
                    <label>${subMember.ExtraFieldTitle}</label>
                </div>
            </div>`;

        const container = Util.createHtmlElementFromTemplate(html);
        for (const [index, value] of predefinedValues.entries()) {
            const type = subMember.IsMultipleSelectionsAllowed ? "checkbox" : "radio";
            const name = subMember.IsMultipleSelectionsAllowed ? "" : `name="chk_${id}"`;
            const isChecked = orderPredefinedValues.includes(value);
            const option = this._createPredefinedValueBox(id, value, index, type, isChecked, name);
            container.appendChild(option);
        }

        return container;
    }

    /**
     * Creates a single predefined extra field checkbox or radio button.
     *
     * @param {string} parentId - The parent DOM id for context.
     * @param {string} value - The label/value of the checkbox.
     * @param {number} index - Index position for unique ID.
     * @param {string} type - Input type ("checkbox" or "radio").
     * @param {boolean} checked - Whether the input should be selected.
     * @param {string} name - Name of the radio boxes.
     * @returns {HTMLElement} The input DOM element.
     */
    private _createPredefinedValueBox(parentId: string, value: string, index: number, type: string, checked: boolean, name: string): HTMLElement {
        const id = `${parentId}_${index}`;
        const html = `
            <div class="group-item-event">
                <div class="checkRadio">
                    <input type="${type}" id="chk_${id}" ${name} class="hide-show-siblings" ${checked ? `checked="true"` : ""} data-value="${value}"/>
                    <label for="chk_${id}">${value}</label>
                </div>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Creates a labeled container element for an open text input field within a challenge event.
     * Typically used to collect additional user input (e.g., names, responses, extra notes).
     *
     * @param {IChallengesOpenFieldData} data - Metadata describing the open field configuration.
     * @returns {HTMLElement} A container element that includes the input field and optional label/buttons.
     */
    private _getEventOpenField(data: IChallengesOpenFieldData): HTMLElement {
        const container = document.createElement("div");
        container.id = data.containerId;
        container.setAttribute("name", data.containerId);
        container.classList.add("challenge-list-group");
        if (!data.preValue)
            container.classList.add("hidden");

        if (data.title) {
            const titleLabel = `
                <div class="group-title">
                    <label class="">${data.title}</label>
                </div>`;
            container.appendChild(Util.createHtmlElementFromTemplate(titleLabel));
        }

        const openField = this._createOpenField(data);
        container.appendChild(openField);
        return container;
    }

    /**
     * Creates a single open input field with optional add/remove (+/-) buttons for dynamic entry lists.
     *
     * @param {IChallengesOpenFieldData} data - Metadata describing the field instance, including index and UI behavior.
     * @returns {HTMLElement} The DOM element representing the input row.
     */
    private _createOpenField(data: IChallengesOpenFieldData): HTMLElement {
        const name = `txt_challenge_open_field_${data.memberId}_${data.eventId}_${(data.subMemberId && data.isExtraField) ? `${data.subMemberId}` : "subMember"}`;
        const id = `${name}_${data.index}`;
        const buttonId = name.replace("txt_", "btn_");

        const html = `
            <div class="group-item-open">
                <input type="text" id="${id}" name="${name}" maxlength="60">
                <button class="add-remove-extra icon-plus ${data.addButton ? "" : "hidden"}" name="${buttonId}_add"></button>
                <button class="add-remove-extra icon-subtract ${data.removeButton ? "" : "hidden"}" name="${buttonId}_remove"></button>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const input = element.querySelector(`#${id}`) as HTMLInputElement;
        if (input)
            input.value = data.preValue || "";

        element.querySelector(`.icon-plus`)?.addEventListener("click", e => this._handleOpenFieldAdd(e, data));
        element.querySelector(`.icon-subtract`)?.addEventListener("click", e => this._handleOpenFieldRemove(e));

        return element;
    }

    /**
     * Filters visible members in the modal based on search input.
     * Matches against `data-text` values in member containers.
     */
    private _searchFunctionality = (): void => {
        const searchText = Util.getElementValue("txt_challenges_search").toLowerCase();
        const memberLabels = Array.from(document.querySelectorAll(".member-check>.checkRadio>label")) as HTMLLabelElement[];

        for (const label of memberLabels) {
            const text = label.innerText?.toLowerCase() || "";
            const parentDiv = label.parentElement?.parentElement;
            if (parentDiv) {
                if ((searchText == "" || text.toLowerCase().includes(searchText)))
                    Util.showElement(parentDiv);
                else
                    Util.hideElement(parentDiv);
            }
        }
    }

    /**
     * Applies the selected challenge and members to the order.
     * If no members are selected, removes any existing challenge.
     */
    private _applyChallenge = (): void => {
        if (!this._item.challenge || !this._validateChallengeValues())
            return;

        if (this._item.members.length == 0) {
            this._orderService.order.removeChallenge();
            this.close();
            return;
        }

        this._orderService.order.setChallenge(this._item.challenge.ChallengeId, this._item.members);
        this.close();
    }

    /**
     * Validates user input for the selected challenge.
     * Ensures all required fields, events, and sub-members are completed correctly.
     * If valid, populates the `this._item.members` array with selected member data.
     *
     * @returns {boolean} `true` if all inputs are valid and collected, otherwise `false`.
     */
    private _validateChallengeValues(): boolean {
        if (!this._item.challenge)
            return false;

        this._item.members = [];

        for (const member of this._item.challenge.Members) {
            const memberCheckbox = document.getElementById(`chk_challenge_member_${member.MemberId}`) as HTMLInputElement;
            if (!memberCheckbox?.checked)
                continue;

            const memberEvents: IOrderChallengeEvent[] = [];
            const businessDate = Util.getBusinessDate(this._data);
            const activeEvent = member.Events.filter(evt => Util.compareDates(new Date(evt.EventDate), businessDate) === 0);

            for (const event of activeEvent) {
                const eventCheckbox = document.getElementById(`chk_challenge_event_${member.MemberId}_${event.EventId}`) as HTMLInputElement;
                if (!eventCheckbox?.checked)
                    continue;

                if (!event.IsAllowCoupons && this._orderService.order.getProperty("Coupons").length > 0) {
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        this._localizationService.names("ERROR_PARTICIPATE_EVENT_REMOVE_COUPON").replaceAll("{{event_name}}", event.EventName),
                        true,
                    );
                    return false;
                }

                if (event.EntryTypeId === ChallengeFieldType.OPEN_FIELD) {
                    const inputs = Array.from(document.getElementsByName(`txt_challenge_open_field_${member.MemberId}_${event.EventId}_subMember`)) as HTMLInputElement[];
                    const subMember = inputs.map(i => i.value.trim()).filter(Boolean).join(", ");

                    if (event.IsRequired && !subMember) {
                        DialogCreators.messageBoxOk(
                            this._localizationService,
                            this._dataLayer,
                            `${event.SubMemberTitle} is required.`,
                            true,
                        );
                        return false;
                    }

                    memberEvents.push({ eventId: event.EventId, subMember, subMemberId: null, extra: null });
                } else if (event.EntryTypeId === ChallengeFieldType.CHECKBOX && event.SubMembers?.length) {
                    let selectedCount = 0;

                    try {
                        for (const sub of event.SubMembers) {
                            const subCheckbox = document.getElementById(`chk_challenge_event_${member.MemberId}_${event.EventId}_${sub.SubMemberId}`) as HTMLInputElement;
                            if (!subCheckbox?.checked)
                                continue;

                            selectedCount++;
                            let extra: string | null = null;

                            if (sub.IsExtraField) {
                                if (!sub.PredefinedValues) {
                                    const openInputs = Array.from(document.getElementsByName(`txt_challenge_open_field_${member.MemberId}_${event.EventId}_${sub.SubMemberId}`)) as HTMLInputElement[];
                                    extra = openInputs.map(i => i.value.trim()).filter(Boolean).join(", ");
                                } else {
                                    const baseId = `challenge_event_${member.MemberId}_${event.EventId}_${sub.SubMemberId}`;
                                    const container = document.getElementById(`div_${baseId}`)!;
                                    const optionInputs = Array.from(container.querySelectorAll(`[id^="chk_${baseId}_"]`)) as HTMLInputElement[];
                                    extra = optionInputs.filter(i => i.checked).map(i => i.dataset.value || "").join(", ");
                                }

                                if (sub.IsRequired && !extra) {
                                    DialogCreators.messageBoxOk(
                                        this._localizationService,
                                        this._dataLayer,
                                        `${sub.ExtraFieldTitle} is required.`,
                                        true
                                    );
                                    return false;
                                }
                            }

                            memberEvents.push({
                                eventId: event.EventId,
                                subMember: sub.SubMemberName,
                                subMemberId: sub.SubMemberId,
                                extra
                            });
                        }

                        if (selectedCount === 0 && event.IsRequired) {
                            DialogCreators.messageBoxOk(
                                this._localizationService,
                                this._dataLayer,
                                `${event.SubMemberTitle} is required.`,
                                true
                            );
                            return false;
                        }
                    } catch {
                        return false;
                    }
                } else {
                    memberEvents.push({
                        eventId: event.EventId,
                        subMember: null,
                        subMemberId: null,
                        extra: null
                    });
                }
            }

            this._item.members.push({
                memberId: member.MemberId,
                events: memberEvents
            });
        }

        return true;
    }

    /**
     * Cleans up event listeners and DOM content associated with the modal.
     * Specifically removes the click listener from the "Apply" button and
     * clears the member list container.
     *
     * @private
     */
    private _removeEventListeners = (): void => {
        Util.setElement("onclick", "btn_challenges_apply", null);

        const membersDiv = document.getElementById("div_challenges_members");
        if (membersDiv)
            membersDiv.innerHTML = "";
    }

    /**
     * Closes the Challenges modal and performs cleanup.
     * Resets internal state and detaches any remaining event listeners.
     *
     * @override
     */
    public override close = (): void => {
        super.close();
        this._options = undefined;
        this._removeEventListeners();
    }
}